import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Clock,
  HelpCircle,
  Mail,
  Phone,
  PhoneOff,
  Plus,
  RefreshCw,
  Search,
  SearchCheck,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserX,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { CopyButton } from "../../components/ui/copy-button";
import { Dropdown } from "../../components/ui/dropdown";
import { SUPPRESSION_REASONS, type SuppressionEntry, type ComplianceMatch } from "./types";

const REASON_OPTIONS = SUPPRESSION_REASONS.map((r) => ({ value: r, label: r }));

function formatDate(iso?: string) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

function reasonBadgeCls(reason: string): string {
  switch (reason) {
    case "Customer opt-out":
      return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20";
    case "DND registry":
      return "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20";
    case "Wrong number":
      return "bg-stone-500/10 text-stone-700 dark:text-stone-300 border-stone-500/20";
    default:
      return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20";
  }
}

export function DndPage() {
  const cache = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [selectedReason, setSelectedReason] = useState<string>("all");
  const [adding, setAdding] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState<string>(SUPPRESSION_REASONS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] = useState<SuppressionEntry | null>(null);

  // Quick compliance lookup modal state
  const [showLookup, setShowLookup] = useState(false);
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupChecking, setLookupChecking] = useState(false);
  const [lookupResult, setLookupResult] = useState<{ checked: boolean; suppressed: boolean; matches: ComplianceMatch[] } | null>(null);

  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  const canManage = team.data?.role === "admin" || !!team.data?.permissions?.assign_leads;

  const list = useQuery({
    queryKey: ["crm-suppression"],
    queryFn: crmApi.suppression,
    enabled: canManage,
  });

  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ["crm-suppression"] });
    toast.success("DND list refreshed", "Suppression table updated.");
  };

  const save = async () => {
    if (saving) return;
    if (!phone.trim() && !email.trim()) {
      setError("Provide a valid phone number or email address.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await crmApi.addSuppression({
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        reason,
      });
      await cache.invalidateQueries({ queryKey: ["crm-suppression"] });
      toast.success("Contact suppressed", "Blocked from automated and manual calling.");
      setAdding(false);
      setPhone("");
      setEmail("");
      setReason(SUPPRESSION_REASONS[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save suppression entry.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    try {
      await crmApi.deleteSuppression(pendingDelete.id);
      await cache.invalidateQueries({ queryKey: ["crm-suppression"] });
      toast.success("Suppression removed", `${pendingDelete.phone || pendingDelete.email} is now clear to call.`);
      setPendingDelete(null);
    } catch (e) {
      toast.danger("Couldn't remove entry", e instanceof Error ? e.message : "Unable to remove.");
      setPendingDelete(null);
    }
  };

  const handleLookup = async () => {
    const q = lookupQuery.trim();
    if (!q) return;
    setLookupChecking(true);
    try {
      const isEmail = q.includes("@");
      const res = await crmApi.complianceCheck(isEmail ? null : q, isEmail ? q : null);
      setLookupResult({
        checked: true,
        suppressed: res.suppressed,
        matches: res.matches || [],
      });
    } catch (err: any) {
      toast.danger("Lookup failed", err.message);
    } finally {
      setLookupChecking(false);
    }
  };

  const entries = useMemo(() => list.data ?? [], [list.data]);
  const dndRegistryCount = entries.filter((e) => e.reason === "DND registry").length;
  const optOutCount = entries.filter((e) => e.reason === "Customer opt-out").length;
  const wrongNumberCount = entries.filter((e) => e.reason === "Wrong number").length;
  const otherCount = entries.filter((e) => e.reason === "Other").length;

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (selectedReason !== "all" && entry.reason !== selectedReason) return false;
      if (!q) return true;
      const phoneMatch = entry.phone?.toLowerCase().includes(q);
      const emailMatch = entry.email?.toLowerCase().includes(q);
      const reasonMatch = entry.reason?.toLowerCase().includes(q);
      return phoneMatch || emailMatch || reasonMatch;
    });
  }, [entries, search, selectedReason]);

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 space-y-4">
      {/* 1. Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-base font-semibold text-ink dark:text-white">DND List</h1>
          {entries.length > 0 && (
            <span className="rounded-full bg-stone-100 dark:bg-stone-800 px-2.5 py-0.5 text-xs font-semibold text-ink-3 dark:text-stone-400">
              {entries.length} blocked
            </span>
          )}
          {/* Header Live Enforcement Status Badge */}
          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            <span>Zero-Call Enforcement Active</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <button
                onClick={() => {
                  setLookupQuery("");
                  setLookupResult(null);
                  setShowLookup(true);
                }}
                className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/10 bg-elevated dark:bg-[#262320] px-3.5 py-1.5 text-xs font-semibold text-ink dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 active:scale-[0.98] transition-all"
              >
                <SearchCheck className="h-3.5 w-3.5 text-brand" />
                <span>Quick Check</span>
              </button>

              <button
                onClick={() => {
                  setError("");
                  setAdding(true);
                }}
                className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Suppress contact</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* 2. Top Summary KPI Cards (5 discrete metrics covering all categories) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Total Blocked */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Total Suppressed</span>
            <PhoneOff className="h-3.5 w-3.5 text-brand" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{entries.length}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">contacts</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">Blocked in both flows</div>
        </div>

        {/* DND Registry */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">DND Registry</span>
            <ShieldAlert className="h-3.5 w-3.5 text-red-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{dndRegistryCount}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">registered</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">Regulatory registry</div>
        </div>

        {/* Customer Opt-outs */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Opt-Outs</span>
            <Ban className="h-3.5 w-3.5 text-amber-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{optOutCount}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">requests</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">Direct customer opt-outs</div>
        </div>

        {/* Wrong Number */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Wrong Number</span>
            <UserX className="h-3.5 w-3.5 text-stone-500 dark:text-stone-400" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{wrongNumberCount}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">reported</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">Invalid / reassigned</div>
        </div>

        {/* Other Reasons */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Other Reasons</span>
            <HelpCircle className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{otherCount}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">custom</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">Manual policy exclusions</div>
        </div>
      </div>

      {/* 3. Dedicated Compliance & Enforcement Safeguard Panel */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-950/20 p-3 sm:px-4 sm:py-3 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-ink dark:text-white">
                Active System Enforcement
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live Protection
              </span>
            </div>
            <p className="text-[11px] text-ink-3 dark:text-stone-400">
              Suppressed contacts are intercepted in real time with zero-bypass across all calling channels.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-ink-2 dark:text-stone-300">
          <span className="inline-flex items-center gap-1 rounded-md border border-line dark:border-white/10 bg-white dark:bg-stone-800 px-2.5 py-1 shadow-2xs">
            <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            Manual Dialer Blocked
          </span>
          <span className="inline-flex items-center gap-1 rounded-md border border-line dark:border-white/10 bg-white dark:bg-stone-800 px-2.5 py-1 shadow-2xs">
            <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            Auto-Campaigns Filtered
          </span>
          <span className="inline-flex items-center gap-1 rounded-md border border-line dark:border-white/10 bg-white dark:bg-stone-800 px-2.5 py-1 shadow-2xs">
            <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            Batch Uploads Screened
          </span>
        </div>
      </div>

      {!canManage && team.data ? (
        <div className="rounded-xl border border-line dark:border-white/10 bg-elevated dark:bg-[#201e1b] p-6 text-center text-sm text-ink-3 dark:text-stone-400">
          The suppression list is managed by sales managers and business administrators.
        </div>
      ) : (
        <>
          {list.error && (
            <div
              role="alert"
              className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 text-xs font-semibold text-red-600 dark:text-red-400"
            >
              {(list.error as Error).message}
            </div>
          )}

          {/* 4. Search and Reason Filter Controls */}
          {entries.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-2.5 shadow-card dark:shadow-card-dark">
              {/* Reason Filters */}
              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => setSelectedReason("all")}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                    selectedReason === "all"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                  )}
                >
                  All ({entries.length})
                </button>
                <button
                  onClick={() => setSelectedReason("DND registry")}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                    selectedReason === "DND registry"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                  )}
                >
                  DND Registry ({dndRegistryCount})
                </button>
                <button
                  onClick={() => setSelectedReason("Customer opt-out")}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                    selectedReason === "Customer opt-out"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                  )}
                >
                  Opt-Outs ({optOutCount})
                </button>
                <button
                  onClick={() => setSelectedReason("Wrong number")}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                    selectedReason === "Wrong number"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                  )}
                >
                  Wrong Number ({wrongNumberCount})
                </button>
                <button
                  onClick={() => setSelectedReason("Other")}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                    selectedReason === "Other"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                  )}
                >
                  Other ({otherCount})
                </button>
              </div>

              {/* Search */}
              <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search phone, email, reason…"
                  className="w-full rounded-lg border border-line dark:border-white/10 bg-white dark:bg-[#1a1816] py-1.5 pl-8 pr-3 text-xs text-ink dark:text-white placeholder:text-ink-3 dark:placeholder:text-stone-500 focus:border-brand focus:outline-none"
                />
              </div>
            </div>
          )}


          {/* 4. Tabular List / Empty States */}
          {!list.isLoading && entries.length === 0 ? (
            /* Clean Empty State */
            <div className="flex flex-col items-center justify-center rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] px-6 py-12 text-center shadow-card dark:shadow-card-dark">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 mb-4">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h2 className="text-base font-semibold text-ink dark:text-white">Suppression List is Clean</h2>
              <p className="mt-1.5 max-w-md text-xs text-ink-3 dark:text-stone-400 leading-relaxed">
                Nobody is currently suppressed. Every contact in the CRM is clear to call in both automated campaigns and agent manual dial workflows.
              </p>
              {canManage && (
                <div className="mt-5 flex items-center gap-2.5">
                  <button
                    onClick={() => {
                      setError("");
                      setAdding(true);
                    }}
                    className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Suppress a contact</span>
                  </button>
                  <button
                    onClick={() => void refresh()}
                    className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/10 bg-white dark:bg-stone-800 px-3.5 py-1.5 text-xs font-semibold text-ink dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 active:scale-[0.98] transition-all"
                  >
                    <RefreshCw className={cn("h-3.5 w-3.5 text-brand", list.isFetching && "animate-spin")} />
                    <span>Refresh</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] shadow-card dark:shadow-card-dark">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-line dark:border-white/10 bg-stone-50 dark:bg-stone-900/60 font-semibold text-ink-3 dark:text-stone-400">
                    <tr>
                      <th className="py-3 px-4">Contact</th>
                      <th className="py-3 px-4">Suppression Reason</th>
                      <th className="py-3 px-4">Enforced Since</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line dark:divide-white/10">
                    {filteredEntries.map((row) => {
                      const isEmail = !!row.email && !row.phone;
                      const contactVal = row.phone || row.email || "—";

                      return (
                        <tr
                          key={row.id}
                          className="transition-colors hover:bg-stone-50/75 dark:hover:bg-white/5"
                        >
                          {/* Contact Info */}
                          <td className="py-3 px-4 align-middle">
                            <div className="flex items-center gap-2">
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-stone-100 dark:bg-stone-800 text-ink-3 dark:text-stone-400">
                                {isEmail ? <Mail className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
                              </span>
                              <span className="font-mono text-xs font-semibold text-ink dark:text-white">
                                {contactVal}
                              </span>
                              <CopyButton value={contactVal} />
                            </div>
                          </td>

                          {/* Reason Badge */}
                          <td className="py-3 px-4 align-middle">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold border",
                                reasonBadgeCls(row.reason),
                              )}
                            >
                              {row.reason}
                            </span>
                          </td>

                          {/* Since Date */}
                          <td className="py-3 px-4 align-middle text-ink-3 dark:text-stone-400">
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3 w-3" />
                              <span>{formatDate(row.createdAt)}</span>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4 align-middle text-right">
                            {canManage && (
                              <button
                                onClick={() => setPendingDelete(row)}
                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold text-ink-3 dark:text-stone-400 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-all"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span>Remove</span>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {filteredEntries.length === 0 && entries.length > 0 && (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-xs text-ink-3 dark:text-stone-400">
                          No suppressed contacts match "{search || selectedReason}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* 5. Suppress Contact Modal */}
      {adding && (
        <Modal
          title="Suppress contact"
          subtitle="Calls to this phone or email will be blocked immediately across automated campaigns and agent workspaces."
          onClose={() => setAdding(false)}
          width="max-w-md"
          footer={
            <div className="flex w-full items-center justify-end gap-2">
              <button
                onClick={() => setAdding(false)}
                className="rounded-lg px-3.5 py-1.5 text-xs font-semibold text-ink-2 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                disabled={saving}
                onClick={() => void save()}
                className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover disabled:opacity-50 transition-all active:scale-[0.98]"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>{saving ? "Saving…" : "Add to list"}</span>
              </button>
            </div>
          }
        >
          <div className="space-y-3.5">
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink dark:text-stone-200">
                Phone Number
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full rounded-lg border border-line dark:border-white/10 bg-white dark:bg-[#1a1816] px-3 py-2 text-xs text-ink dark:text-white placeholder:text-ink-3 dark:placeholder:text-stone-500 focus:border-brand focus:outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-ink dark:text-stone-200">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="client@company.in"
                className="w-full rounded-lg border border-line dark:border-white/10 bg-white dark:bg-[#1a1816] px-3 py-2 text-xs text-ink dark:text-white placeholder:text-ink-3 dark:placeholder:text-stone-500 focus:border-brand focus:outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-ink dark:text-stone-200">
                Reason *
              </label>
              <Dropdown
                value={reason}
                options={REASON_OPTIONS}
                onChange={(val) => setReason(val)}
                ariaLabel="Suppression Reason"
                className="w-full"
              />
            </div>

            {error && (
              <p role="alert" className="text-xs font-semibold text-red-600 dark:text-red-400">
                {error}
              </p>
            )}

            <div className="rounded-lg bg-stone-50 dark:bg-white/5 border border-line dark:border-white/10 p-2.5 text-[11px] text-ink-3 dark:text-stone-400 leading-relaxed">
              <span className="font-semibold text-ink dark:text-stone-200">Compliance note:</span> Adding a contact immediately suppresses them from outgoing agent calls and removes them from queued auto-dial runs.
            </div>
          </div>
        </Modal>
      )}

      {/* 6. Quick Compliance Lookup Modal */}
      {showLookup && (
        <Modal
          title="Compliance Check"
          subtitle="Check whether a phone number or email is currently blocked from being dialed."
          onClose={() => setShowLookup(false)}
          width="max-w-md"
          footer={
            <button
              onClick={() => setShowLookup(false)}
              className="rounded-lg px-3.5 py-1.5 text-xs font-semibold text-ink-2 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/5"
            >
              Close
            </button>
          }
        >
          <div className="space-y-3.5">
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink dark:text-stone-200">
                Enter Phone or Email
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={lookupQuery}
                  onChange={(e) => {
                    setLookupQuery(e.target.value);
                    setLookupResult(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && void handleLookup()}
                  placeholder="e.g. +91 9876543210 or user@example.com"
                  className="flex-1 rounded-lg border border-line dark:border-white/10 bg-white dark:bg-[#1a1816] px-3 py-2 text-xs text-ink dark:text-white placeholder:text-ink-3 dark:placeholder:text-stone-500 focus:border-brand focus:outline-none"
                />
                <button
                  onClick={() => void handleLookup()}
                  disabled={!lookupQuery.trim() || lookupChecking}
                  className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover disabled:opacity-50 transition-all"
                >
                  <Search className="h-3.5 w-3.5" />
                  <span>{lookupChecking ? "Checking…" : "Check"}</span>
                </button>
              </div>
            </div>

            {lookupResult && (
              <div
                className={cn(
                  "rounded-xl border p-4 transition-all",
                  lookupResult.suppressed
                    ? "border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20"
                    : "border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-950/20",
                )}
              >
                <div className="flex items-center gap-2.5">
                  {lookupResult.suppressed ? (
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-500/10 text-red-600 dark:text-red-400">
                      <Ban className="h-4 w-4" />
                    </div>
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                  )}

                  <div>
                    <h3
                      className={cn(
                        "text-xs font-semibold",
                        lookupResult.suppressed
                          ? "text-red-700 dark:text-red-400"
                          : "text-emerald-700 dark:text-emerald-400",
                      )}
                    >
                      {lookupResult.suppressed ? "SUPPRESSED — DO NOT CALL" : "CLEAR TO CALL"}
                    </h3>
                    <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5">
                      {lookupResult.suppressed
                        ? "This contact is registered on the suppression list."
                        : "No suppression records found for this contact."}
                    </p>
                  </div>
                </div>

                {lookupResult.matches.length > 0 && (
                  <div className="mt-3 divide-y divide-red-200/60 dark:divide-red-900/40 border-t border-red-200/60 dark:border-red-900/40 pt-2">
                    {lookupResult.matches.map((m, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1 text-[11px]">
                        <span className="font-semibold text-red-700 dark:text-red-300">{m.reason}</span>
                        <span className="text-ink-3 dark:text-stone-400">{formatDate(m.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* 7. Remove Confirmation Modal */}
      {pendingDelete && (
        <ConfirmModal
          title="Remove from suppression list"
          body={`Allow calls to "${pendingDelete.phone || pendingDelete.email}" again? This will re-enable outbound dialing for this contact.`}
          confirmLabel="Remove"
          onConfirm={() => void remove()}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

